<template>
  <v-card :loading="loading" class="pa-5 rounded-lg" outlined>
    <v-autocomplete
      v-model="selectedAssistant"
      :items="assistants"
      :search-input.sync="searchQuery"
      item-text="name"
      item-value="id"
      return-object
      :disabled="loading"
      :placeholder="$t('search_assistant')"
      :label="$t('select_assistant')"
      outlined
      dense
      clearable
      hide-no-data
      :menu-props="{ maxHeight: 320, offsetY: true }"
    >
      <!-- Custom item template -->
      <template v-slot:item="{ item, on, attrs }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          :disabled="isExpired(item.expired_at)"
          :class="{ 'expired-item': isExpired(item.expired_at) }"
        >
          <v-list-item-content @click="connectToAi(item.id)">
            <v-list-item-title class="d-flex align-center">
              <strong>{{ item.name }}</strong>
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.type" class="text-caption">
              {{ item.type }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  :color="getStatusColor(item.expired_at)"
                  size="20"
                  @click.stop="onExpiryIconClick(item)"
                >
                  {{ getStatusIcon(item.expired_at) }}
                </v-icon>
              </template>
              <span>{{ getExpiryTooltip(item.expired_at) }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </template>

      <!-- Custom selection display -->
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-icon
            :color="getStatusColor(item.expired_at)"
            size="18"
            class="ml-1"
          >
            {{ getStatusIcon(item.expired_at) }}
          </v-icon>
          <span class="mr-2">{{ item.name }}</span>
        </div>
      </template>

      <!-- Add new assistant button at bottom -->
      <template v-slot:append-item>
        <v-divider class="my-2" />
        <v-btn
          @click="goToCreateAssistant"
          class="btn-get-code rounded-lg dwidth"
        >
          افزودن<v-icon>mdi-plus</v-icon>
        </v-btn>

        <!-- <v-list-item @click="goToCreateAssistant" class="primary--text">
          <v-list-item-icon>
            <v-icon color="primary">mdi-plus-circle</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="primary--text font-weight-medium">
              {{ $t("add_new_assistant") }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item> -->
      </template>
    </v-autocomplete>
    <upgrade-ai
      v-model="showUpgradeDialog"
      :current-package="currentPackage"
      @input="showUpgradeDialog = $event"
      :assistant-id="selectedAssistantId"
      @upgraded="onUpgradeSuccess"
    />
  </v-card>
</template>

<script>
import upgradeAi from "./upgradeAi.vue";
import { mapGetters } from "vuex";

export default {
  name: "assistantAiList",

  props: {
    value: {
      type: [Object, Number, String],
      default: null,
    },
  },
  components: {
    upgradeAi,
  },   

  data() {
    return {
      currentPackage: null,
      showUpgradeDialog: false,
      selectedAssistant: null,
      selectedAssistantId: null,
      assistants: [],
      loading: false,
      searchQuery: "",
    };
  },

  computed: {
    ...mapGetters("localstorage", ["createQrID"]),
  },

  watch: {
    selectedAssistant: {
      handler(newVal) {
        if (newVal && !this.isExpired(newVal.expired_at)) {
          this.$emit("input", newVal.id);
          this.$emit("change", newVal);
        } else if (!newVal) {
          this.$emit("input", null);
          this.$emit("change", null);
        }
      },
      deep: true,
    },

    assistants(newVal) {
      if (this.value && Array.isArray(newVal)) {
        const found = newVal.find((a) => a.id === this.value);
        if (
          found &&
          (!this.selectedAssistant || this.selectedAssistant.id !== found.id)
        ) {
          this.selectedAssistant = found;
        }
      }
    },

    value: {
      handler(newVal) {
        if (
          newVal &&
          Array.isArray(this.assistants) &&
          this.assistants.length > 0
        ) {
          const found = this.assistants.find((a) => a.id === newVal);
          if (
            found &&
            (!this.selectedAssistant || this.selectedAssistant.id !== found.id)
          ) {
            this.selectedAssistant = found;
          }
        } else if (!newVal) {
          this.selectedAssistant = null;
        }
      },
      immediate: true,
    },
  },

  created() {
    this.fetch();
  },

  methods: {
    connectToAi(assistantid) {
      if (assistantid === null || assistantid === undefined) {
        this.$toast.error(
          "خطا در ارتباط با کیوار. لطفا با پشتیبانی تماس بگیرید.",
        );
        return;
      }
      //api/assistant/qr
      this.loading = true;
      this.$api
        .post(`${this.$aiApi}/assistant/qr`, {
          qr_id: this.createQrID,
          assist_id: assistantid,
        })
        .then(({ data }) => {})
        .catch((err) => {
          console.error("Error connect to ai assistants:", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onUpgradeSuccess() {
      this.showUpgradeDialog = false;
      this.fetch();
    },
    buyAi(assistant) {
      this.currentPackage = assistant;
      this.selectedAssistantId = assistant.id;
      this.showUpgradeDialog = true;
    },
    isExpired(expiredAt) {
      if (!expiredAt) return true;
      const expiredDate = new Date(expiredAt);
      const now = new Date();
      return expiredDate < now;
    },

    getDaysRemaining(expiredAt) {
      if (!expiredAt) return null;
      const expiredDate = new Date(expiredAt);
      const now = new Date();
      const diffTime = expiredDate - now;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    getStatusColor(expiredAt) {
      if (!expiredAt) return "grey";
      const diffDays = this.getDaysRemaining(expiredAt);

      if (diffDays < 0) {
        // منقضی شده
        return "error";
      } else if (diffDays === 0) {
        // امروز
        return "error";
      } else if (diffDays <= 3) {
        // کمتر از 3 روز
        return "deep-orange";
      } else if (diffDays <= 7) {
        // کمتر از یک هفته
        return "orange";
      } else if (diffDays <= 15) {
        // کمتر از 15 روز
        return "amber";
      } else if (diffDays <= 30) {
        // کمتر از یک ماه
        return "lime";
      } else {
        // بیش از یک ماه
        return "success";
      }
    },

    getStatusIcon(expiredAt) {
      if (!expiredAt) return "mdi-clock-outline";
      const diffDays = this.getDaysRemaining(expiredAt);

      if (diffDays < 0) {
        return "mdi-clock-alert";
      } else if (diffDays <= 3) {
        return "mdi-clock-alert-outline";
      } else if (diffDays <= 7) {
        return "mdi-clock-fast";
      } else if (diffDays <= 15) {
        return "mdi-clock-outline";
      } else {
        return "mdi-clock-check-outline";
      }
    },
    getExpiryTooltip(expiredAt) {
      if (!expiredAt) return this.$t("no_expiry_date");

      const expiredDate = new Date(expiredAt);
      const now = new Date();
      const diffTime = expiredDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const formattedDate = expiredDate.toLocaleDateString("fa-IR");

      if (diffTime < 0) {
        return `${this.$t("expired")} - ${formattedDate}`;
      }
      if (
        diffDays === 0 ||
        (diffDays === 1 && diffTime < 24 * 60 * 60 * 1000)
      ) {
        const hours = Math.floor(diffTime / (1000 * 60 * 60));
        const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
          if (minutes > 0) {
            return `${hours} ${this.$t("hour")} و ${minutes} ${this.$t(
              "minute",
            )} ${this.$t("remaining")} - ${formattedDate}`;
          } else {
            return `${hours} ${this.$t("hour")} ${this.$t(
              "remaining",
            )} - ${formattedDate}`;
          }
        } else {
          return `${minutes} ${this.$t("minute")} ${this.$t(
            "remaining",
          )} - ${formattedDate}`;
        }
      }
      if (diffDays === 1) {
        return `${this.$t("one_day_remaining")} - ${formattedDate}`;
      }

      if (diffDays <= 7) {
        return `${diffDays} ${this.$t("days_remaining")} - ${formattedDate}`;
      }

      if (diffDays <= 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} ${this.$t("weeks_remaining")} - ${formattedDate}`;
      }

      const months = Math.floor(diffDays / 30);
      return `${months} ${this.$t("months_remaining")} - ${formattedDate}`;
    },
    onExpiryIconClick(item) {
      this.buyAi(item);
    },

    goToCreateAssistant() {
      window.open("https://core-ai.termalads.com/#/assiastantAi", "_blank");
    },

    fetch() {
      this.loading = true;
      this.$api
        .get(`${this.$aiApi}/assistant`, {
          params: {
            search: "",
            column: "id",
            dir: "desc",
            page: 1,
            length: 9999999999,
          },
        })
        .then(({ data }) => {
          this.assistants = data.data.data || [];
        })
        .catch((err) => {
          console.error("Error fetching assistants:", err);
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "خطا در دریافت لیست دستیارها";
          this.$toast.error(errorMessage);
          this.assistants = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.expired-item {
  opacity: 0.6;
  background-color: rgba(244, 67, 54, 0.05);
}

.expired-item:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.v-list-item--disabled {
  pointer-events: auto !important;
}
.dwidth {
  width: 95%;
  margin-left: 8px !important;
  margin-right: 8px !important;
}
</style>
